import {
  Boxes,
  Bot,
  BrainCircuit,
  CalendarClock,
  ChevronRight,
  Headphones,
  Workflow as WorkflowIcon,
  PhoneOutgoing,
  ReceiptText,
  Sparkles,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Modal } from "../../components/ui/modal";
import { core } from "../../lib/api";
import { cn } from "../../lib/utils";

interface PromptTemplate {
  id: string;
  name: string;
  direction: "inbound" | "outbound";
  description: string;
  agent_name: string;
  use_case: string;
}

const templateIcons = {
  "customer-support": Headphones,
  "sales-qualification": PhoneOutgoing,
  collections: ReceiptText,
  "appointment-booking": CalendarClock,
} as const;

type CreationAction = "autobuilder" | "single" | "workflow" | string;

export function CreateAgentModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const templates = useQuery({
    queryKey: ["prompt-builder", "templates"],
    queryFn: () => core<PromptTemplate[]>("/api/v1/prompt-builder/templates"),
    staleTime: 5 * 60_000,
  });

  const openAutoBuilder = (templateId?: string) => {
    onClose();
    navigate(`/home/autobuilder${templateId ? `?template_id=${encodeURIComponent(templateId)}` : ""}`);
  };

  const openSingleAgent = () => {
    onClose();
    navigate("/home/create-agent?new=1");
  };

  const openThinkerTalker = () => {
    onClose();
    navigate("/home/create-agent?new=1&architecture=thinker_talker");
  };

  const openWorkflow = () => {
    onClose();
    navigate("/home/create-workflow?new=1");
  };

  const modeButton = (
    action: CreationAction,
    icon: typeof Sparkles,
    title: string,
    description: string,
    cta: string,
    onClick: () => void,
    recommended = false,
  ) => {
    const Icon = icon;
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "group min-h-[154px] border border-line bg-elevated p-4 text-left transition-colors hover:border-brand hover:bg-brand-soft/20",
          recommended && "border-brand/40",
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <span className={cn(
            "flex h-9 w-9 items-center justify-center rounded-md bg-soft text-ink-2",
            recommended && "bg-brand-soft text-brand",
          )}>
            <Icon className="h-4.5 w-4.5" />
          </span>
          {recommended && (
            <span className="rounded bg-brand-soft px-2 py-1 text-[10px] font-bold uppercase text-brand">
              Recommended
            </span>
          )}
        </div>
        <h3 className="mt-4 text-sm font-bold text-ink">{title}</h3>
        <p className="mt-1 min-h-10 text-xs leading-5 text-ink-2">{description}</p>
        <span className={cn(
          "mt-3 inline-flex items-center gap-1 text-xs font-bold text-ink",
          recommended && "text-brand",
        )}>
          {cta}
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </button>
    );
  };

  return (
    <Modal
      title="Create a new agent"
      subtitle="Select an agent architecture to get started."
      onClose={onClose}
      width="max-w-5xl"
    >
      <div className="grid gap-px overflow-hidden rounded-md border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
        {modeButton(
          "autobuilder",
          Sparkles,
          "AutoBuilder",
          "Generate a complete prompt, variables, tools, and guardrails from a job description.",
          "Start guided build",
          () => openAutoBuilder(),
          true,
        )}
        {modeButton(
          "single",
          Bot,
          "Single agent",
          "Manually configure a prompt, call fields, tools, and runtime settings.",
          "Open agent editor",
          openSingleAgent,
        )}
        {modeButton(
          "thinker-talker",
          BrainCircuit,
          "Thinker-Talker",
          "Separate fast conversation delivery from deep reasoning and routing.",
          "Open reasoning editor",
          openThinkerTalker,
        )}
        {modeButton(
          "workflow",
          WorkflowIcon,
          "Workflow",
          "Design complex routing logic across multiple agents and tools.",
          "Open workflow canvas",
          openWorkflow,
        )}
      </div>

      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1 bg-line" />
        <span className="font-mono text-[10px] font-bold uppercase text-ink-3">
          Or start AutoBuilder with a template
        </span>
        <div className="h-px flex-1 bg-line" />
      </div>

      {templates.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="h-24 animate-pulse rounded-md bg-soft" />
          ))}
        </div>
      ) : templates.isError ? (
        <div className="flex items-center justify-between border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          Templates could not be loaded.
          <button type="button" className="font-bold underline" onClick={() => templates.refetch()}>
            Retry
          </button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {(templates.data ?? []).map((template) => {
            const Icon = templateIcons[template.id as keyof typeof templateIcons] || Boxes;
            return (
              <button
                key={template.id}
                type="button"
                onClick={() => openAutoBuilder(template.id)}
                className="group flex min-h-[92px] items-center gap-3 rounded-md border border-line bg-elevated p-3 text-left transition-colors hover:border-brand hover:bg-soft"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-soft text-ink-2 group-hover:bg-brand-soft group-hover:text-brand">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-bold text-ink">{template.name}</span>
                    <span className="rounded bg-soft px-1.5 py-0.5 text-[9px] font-bold uppercase text-ink-3">
                      {template.direction}
                    </span>
                  </span>
                  <span className="mt-1 block text-xs leading-4 text-ink-2">{template.description}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-3 group-hover:text-brand" />
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
